type LegalQuickLink = {
  href: string;
  label: string;
};

type LegalEnhancementOptions = {
  fallbackUpdatedAt: string;
  quickLinks: LegalQuickLink[];
  tocTitle?: string;
};

const escapeScriptCloseTag = (value: string) => value.replaceAll('</script', '<\\/script');

const buildEnhancerScript = ({
  fallbackUpdatedAt,
  quickLinks,
  tocTitle = 'On this page',
}: LegalEnhancementOptions) => {
  const config = JSON.stringify({
    fallbackUpdatedAt,
    quickLinks,
    tocTitle,
  });

  return `
    (function () {
      var CONFIG = ${config};
      var page = document.querySelector('.tp-static-page article.page');
      if (!page) {
        return;
      }

      if (page.getAttribute('data-legal-enhanced') === 'true') {
        return;
      }
      page.setAttribute('data-legal-enhanced', 'true');

      var header = page.querySelector('header');
      var pageBody = page.querySelector('.page-body');
      if (!pageBody) {
        return;
      }

      var normalizeText = function (value) {
        return (value || '').replace(/\\s+/g, ' ').trim();
      };

      var getNextMeaningfulText = function (node) {
        var cursor = node;
        while (cursor) {
          cursor = cursor.nextElementSibling;
          if (!cursor) {
            return '';
          }
          var nextText = normalizeText(cursor.textContent || '');
          if (nextText) {
            return nextText;
          }
        }
        return '';
      };

      var extractUpdatedAt = function () {
        var markers = [
          'effective date',
          'last updated',
          'updated on',
          'дата вступления в силу',
          'дата обновления',
        ];
        var nodes = pageBody.querySelectorAll('h1, h2, h3, h4, p, strong, em, span');

        for (var i = 0; i < nodes.length; i += 1) {
          var node = nodes[i];
          var nodeText = normalizeText(node.textContent || '');
          var normalized = nodeText.toLowerCase();

          var matched = markers.some(function (marker) {
            return normalized === marker || normalized.indexOf(marker + ':') === 0;
          });

          if (!matched) {
            continue;
          }

          var nextText = getNextMeaningfulText(node);
          if (nextText && nextText.length <= 120) {
            return nextText;
          }
        }

        return CONFIG.fallbackUpdatedAt;
      };

      var createMetaBar = function () {
        var bar = document.createElement('div');
        bar.className = 'tp-legal-meta-bar';

        var updated = document.createElement('p');
        updated.className = 'tp-legal-updated';
        updated.textContent = 'Last updated: ' + extractUpdatedAt();
        bar.appendChild(updated);

        if (Array.isArray(CONFIG.quickLinks) && CONFIG.quickLinks.length > 0) {
          var nav = document.createElement('nav');
          nav.className = 'tp-legal-links';
          nav.setAttribute('aria-label', 'Legal links');

          CONFIG.quickLinks.forEach(function (entry) {
            if (!entry || !entry.href || !entry.label) {
              return;
            }

            var link = document.createElement('a');
            link.href = entry.href;
            link.textContent = entry.label;
            nav.appendChild(link);
          });

          bar.appendChild(nav);
        }

        if (header) {
          header.insertAdjacentElement('afterend', bar);
        } else {
          page.prepend(bar);
        }
      };

      var setupToc = function () {
        var headings = Array.prototype.slice
          .call(pageBody.querySelectorAll('h1, h2, h3'))
          .filter(function (heading) {
            return normalizeText(heading.textContent || '').length > 0;
          });

        if (headings.length < 3) {
          return;
        }

        headings.forEach(function (heading, index) {
          if (!heading.id) {
            heading.id = 'legal-section-' + String(index + 1);
          }
        });

        var layout = document.createElement('div');
        layout.className = 'tp-legal-layout';
        pageBody.insertAdjacentElement('beforebegin', layout);

        var toc = document.createElement('aside');
        toc.className = 'tp-legal-toc';
        toc.setAttribute('aria-label', 'Table of contents');

        var title = document.createElement('p');
        title.className = 'tp-legal-toc-title';
        title.textContent = CONFIG.tocTitle || 'On this page';
        toc.appendChild(title);

        var list = document.createElement('ol');
        list.className = 'tp-legal-toc-list';

        headings.forEach(function (heading) {
          var item = document.createElement('li');
          item.className = 'tp-legal-toc-item';

          var link = document.createElement('a');
          link.className = 'tp-legal-toc-link';
          link.href = '#' + heading.id;
          link.textContent = normalizeText(heading.textContent || '');
          link.setAttribute('data-target-id', heading.id);

          item.appendChild(link);
          list.appendChild(item);
        });

        toc.appendChild(list);
        layout.appendChild(toc);
        layout.appendChild(pageBody);

        var links = Array.prototype.slice.call(list.querySelectorAll('.tp-legal-toc-link'));
        var activateLink = function (id) {
          links.forEach(function (link) {
            var isActive = link.getAttribute('data-target-id') === id;
            link.classList.toggle('active', isActive);
          });
        };

        var firstId = headings[0]?.id;
        if (firstId) {
          activateLink(firstId);
        }

        if ('IntersectionObserver' in window) {
          var observer = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                  activateLink(entry.target.id);
                }
              });
            },
            {
              threshold: 0.2,
              rootMargin: '-20% 0px -70% 0px',
            },
          );

          headings.forEach(function (heading) {
            observer.observe(heading);
          });
        }
      };

      createMetaBar();
      setupToc();
    })();
  `;
};

export const withLegalEnhancements = (rawHtml: string, options: LegalEnhancementOptions) => {
  const script = buildEnhancerScript(options);
  return `${rawHtml}\n<script>${escapeScriptCloseTag(script)}</script>`;
};
